import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, deDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { DocScan } from "../../components/DocScan";
import { Plus, Download, FileText, ScanText } from "lucide-react";

const TYPES = { ankaufvertrag: "Ankaufvertrag", rechnung: "Rechnung", gutschrift: "Gutschrift", verkaeuferdokument: "Verkäuferdokument", ausweisdokument: "Ausweisdokument", produktdokument: "Produktdokument", verkaufsdokument: "Verkaufsdokument", sonstiges: "Sonstiges" };

export default function Dokumente() {
  const [docs, setDocs] = useState([]);
  const [type, setType] = useState("");
  const [open, setOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [form, setForm] = useState({ title: "", type: "sonstiges" });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const [params] = useSearchParams();

  useEffect(() => { if (params.get("scan")) setScanOpen(true); }, [params]);

  const load = () => api.get(`/documents${type ? `?type=${type}` : ""}`).then((r) => setDocs(r.data));
  useEffect(() => { load(); }, [type]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, base64: reader.result });
    reader.readAsDataURL(f);
  };

  const save = async () => {
    try {
      await api.post("/documents", { ...form, file_name: file?.name, file_data: file?.base64 });
      toast.success("Dokument gespeichert.");
      setOpen(false); setFile(null); setForm({ title: "", type: "sonstiges" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const download = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/download`, "_blank");

  return (
    <div className="space-y-6" data-testid="dokumente-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Ablage</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Dokumente</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setScanOpen(true)} className="min-h-[44px]" data-testid="scan-document-button"><ScanText className="w-4 h-4 mr-1" /> Scannen</Button>
          <Button onClick={() => setOpen(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white min-h-[44px]" data-testid="new-document-button"><Plus className="w-4 h-4 mr-1" /> Hochladen</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setType("")} data-testid="doc-filter-all" className={`text-xs font-mono px-3 py-1.5 rounded-full border ${!type ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-500"}`}>Alle</button>
        {Object.entries(TYPES).map(([k, l]) => (
          <button key={k} onClick={() => setType(k)} data-testid={`doc-filter-${k}`} className={`text-xs font-mono px-3 py-1.5 rounded-full border ${type === k ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-500 hover:border-zinc-400"}`}>{l}</button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="border-b border-zinc-100 text-left">
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Dokument</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Typ</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Vorgang</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Datum</th>
            <th className="px-5 py-3"></th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-50">
            {docs.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-400">Keine Dokumente.</td></tr>}
            {docs.map((d) => (
              <tr key={d.id} className="hover:bg-zinc-50" data-testid={`document-row-${d.id}`}>
                <td className="px-5 py-3 flex items-center gap-2"><FileText className="w-4 h-4 text-zinc-400" /> {d.title}</td>
                <td className="px-5 py-3"><span className="text-[10px] font-mono bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-full">{TYPES[d.type] || d.type}</span></td>
                <td className="px-5 py-3 font-mono text-xs text-zinc-500">{d.ref_number || "–"}</td>
                <td className="px-5 py-3 text-zinc-500">{deDate(d.created_at)}</td>
                <td className="px-5 py-3 text-right">
                  {d.file_name && <Button variant="outline" size="sm" onClick={() => download(d.id)} data-testid={`document-download-${d.id}`}><Download className="w-3.5 h-3.5 mr-1" /> {d.file_name}</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DocScan open={scanOpen} onClose={() => setScanOpen(false)} onSaved={() => load()} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="document-upload-dialog">
          <DialogHeader><DialogTitle>Dokument hochladen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titel</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="document-title" className="mt-1.5" /></div>
            <div><Label>Typ</Label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="document-type" className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                {Object.entries(TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-zinc-300 rounded-lg p-6 text-center cursor-pointer hover:bg-zinc-50 text-sm text-zinc-400" data-testid="document-file-drop">
              {file ? <span className="font-mono text-zinc-700">{file.name}</span> : "Datei auswählen"}
            </div>
            <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="document-file-input" />
            <Button onClick={save} disabled={!form.title || !file} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="document-save-button">Hochladen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
